import React, { useState, useEffect } from 'react';
import { UserPlus, Search, SlidersHorizontal, X, Mail } from 'lucide-react';
import { unlinkEmployee } from '../../api/invitations';
import { useUsers } from './hooks/useUsers';
import { UserForm, UserTable, PasswordResetModal } from './components';
import Pagination from '../common/Pagination';
import { getRoles } from '../../api/roles';
import InvitationsTab from './components/InvitationsTab';

const PAGE_SIZE = 10;

export default function UsuariosModule() {
  const { users, createUser, updateUser, toggleUserStatus, resetPassword, deleteUser, getStats, refreshUsers } = useUsers();

  const [showForm, setShowForm]             = useState(false);
  const [editingUser, setEditingUser]       = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser]     = useState(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [filterRol, setFilterRol]           = useState('todos');
  const [filterEstado, setFilterEstado]     = useState('todos');
  const [showFilters, setShowFilters]       = useState(false);
  const [page, setPage]                     = useState(1);
  const [roles, setRoles]                   = useState([]);
  const [activeTab, setActiveTab]           = useState('usuarios');

  const stats = getStats();

  useEffect(() => {
    getRoles().then(r => setRoles(r.roles || [])).catch(() => {});
  }, []);

  const handleNewUser = () => { setEditingUser(null); setShowForm(true); };
  const handleEdit    = (user) => { setEditingUser(user); setShowForm(true); };
  const handleCancel  = () => { setShowForm(false); setEditingUser(null); };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
      } else {
        await createUser(formData);
      }
      setShowForm(false);
      setEditingUser(null);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleToggleStatus = async (userId) => {
    const user = users.find(u => u.id === userId);
    const action = user.companyActivo ? 'desactivar' : 'activar';
    if (confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} a ${user.username} en esta compañía?`)) {
      try { await toggleUserStatus(userId); } catch (e) { alert(e.message); }
    }
  };

  const handleResetPasswordClick = (userId) => {
    setSelectedUser(users.find(u => u.id === userId));
    setShowPasswordModal(true);
  };

  const handlePasswordReset = async (newPassword) => {
    try {
      await resetPassword(selectedUser.id, newPassword);
      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (e) { alert(e.message); }
  };

  const handleUnlink = async (user) => {
    if (!window.confirm(`¿Desvincular a ${user.username} del empleado ${user.employeeNombre}?\nEl usuario quedará activo pero sin acceso al sistema.`)) return;
    try {
      await unlinkEmployee(user.employee_id);
      refreshUsers();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al desvincular');
    }
  };

  const handleDelete = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (confirm(`¿Eliminar usuario ${user.username}? Esta acción no se puede deshacer.`)) {
      try { await deleteUser(userId); } catch (e) { alert(e.message); }
    }
  };

  const handleSearch  = (val) => { setSearchTerm(val);   setPage(1); };
  const handleRol     = (val) => { setFilterRol(val);    setPage(1); };
  const handleEstado  = (val) => { setFilterEstado(val); setPage(1); };

  const filtered = users.filter(u => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      u.username?.toLowerCase().includes(q) ||
      u.nombre?.toLowerCase().includes(q) ||
      u.apellido?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q);
    const matchRol    = filterRol    === 'todos' || u.rol    === filterRol;
    const matchEstado = filterEstado === 'todos' || (filterEstado === 'activo' ? u.companyActivo : !u.companyActivo);
    return matchSearch && matchRol && matchEstado;
  });

  const hasFilter  = filterRol !== 'todos' || filterEstado !== 'todos';
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Form (full-page) ─────────────────────────────────────────
  if (showForm) {
    return (
      <UserForm
        user={editingUser}
        roles={roles}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Tabs ────────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-gray-200 pb-0">
        {[
          { id: 'usuarios',     label: 'Usuarios',     icon: UserPlus },
          { id: 'invitaciones', label: 'Invitaciones', icon: Mail     },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors -mb-px border-b-2 ${
                activeTab === tab.id
                  ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <Icon size={15} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab Invitaciones ────────────────────────────────────── */}
      {activeTab === 'invitaciones' && <InvitationsTab />}

      {/* ── Tab Usuarios ────────────────────────────────────────── */}
      {activeTab === 'usuarios' && <>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {filtered.length} de {users.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewUser}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlus size={15} />
            Nuevo usuario
          </button>
        </div>
      </div>

      {/* ── Search + Filter bar ─────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar usuario..."
            value={searchTerm}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button onClick={() => handleSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
              <X size={14} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
            showFilters || hasFilter
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filtros
          {hasFilter && <span className="w-2 h-2 rounded-full bg-blue-500" />}
        </button>

        <div className="hidden sm:flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-green-600">{stats.activos}</span> activos
          </span>
          <span className="text-gray-200">|</span>
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-red-500">{stats.inactivos}</span> inactivos
          </span>
          <span className="text-gray-200">|</span>
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-blue-600">{stats.total}</span> total
          </span>
        </div>
      </div>

      {/* ── Filter panel ────────────────────────────────────────── */}
      {showFilters && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg flex-wrap">
          <select
            value={filterRol}
            onChange={e => handleRol(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="todos">Todos los roles</option>
            {roles.map(r => (
              <option key={r.id} value={r.codigo}>{r.nombre}</option>
            ))}
          </select>
          <select
            value={filterEstado}
            onChange={e => handleEstado(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="todos">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
          {hasFilter && (
            <button
              onClick={() => { handleRol('todos'); handleEstado('todos'); }}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      )}

      {/* ── Table + Pagination ──────────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <UserTable
          users={paginated}
          onEdit={handleEdit}
          onToggleStatus={handleToggleStatus}
          onResetPassword={handleResetPasswordClick}
          onDelete={handleDelete}
          onUnlink={handleUnlink}
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          total={filtered.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      {/* ── Password modal ──────────────────────────────────────── */}
      {showPasswordModal && selectedUser && (
        <PasswordResetModal
          user={selectedUser}
          onReset={handlePasswordReset}
          onClose={() => { setShowPasswordModal(false); setSelectedUser(null); }}
        />
      )}
      </>}
    </div>
  );
}
